import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useAnalyticsReportBase } from '../../../hooks/useAnalyticsReportBase'
import { useExportAnalyticsReportCSV } from '../../../hooks/useExportAnalyticsReportCSV'
import { useReportSeries } from '../../../hooks/useReportSeries'
import { useReportTableSorting } from '../../../hooks/useReportTableSorting'
import { useAnalyticsParams } from '../../../modules/AnalyticsParams/hooks/useAnalyticsParams'
import { useAnalyticsContext } from '../../../providers/AnalyticsProvider'
import { getExternalSource } from '../../../utils/getExternalSource'
import { useAnalyticsReportCard } from './useAnalyticsReportCard'
import { useAnalyticsReportFilters } from './useAnalyticsReportFilters'
import { useAnalyticsReportTable } from './useAnalyticsReportTable'
import { createAnalyticsTableColumns } from './utils/createAnalyticsTableColumns'

export const useAnalyticsReport = () => {
  const { reportId } = useParams<{ reportId: string }>()
  const { exportReportCSV } = useExportAnalyticsReportCSV()
  const [isExporting, setIsExporting] = useState(false)

  const { getReportDefinition, isLoading: isReportDefinitionLoading } = useAnalyticsContext()
  const reportDefinition = getReportDefinition(reportId || '')

  const {
    filters,
    filtersForAPI,
    filtersForVisibility,
    decorationOverrides,
    onChange: onFiltersChange,
    hasActiveFilters,
  } = useAnalyticsReportFilters({
    reportDefinition,
  })

  const { reportData, isReportDataLoading, supportsPrecisions, supportedPrecisions } =
    useAnalyticsReportBase({
      reportId: reportId || '',
      filters: filtersForAPI,
    })
  const params = useAnalyticsParams({ supportedPrecisions })

  const columns = useMemo(
    () =>
      createAnalyticsTableColumns({
        definitionColumns: reportDefinition?.columns || [],
        precision: params.precision.value.value,
        decorationOverrides,
        filtersForVisibility,
      }),
    [
      reportDefinition?.columns,
      params.precision.value.value,
      decorationOverrides,
      filtersForVisibility,
    ],
  )
  const { activeSeries, setActiveSeries, headerOptions } = useReportSeries(
    columns,
    reportDefinition?.yAxisDefaultValue || '',
  )
  const { sorting, onSortingChange } = useReportTableSorting({
    columns,
    defaultSorting: reportDefinition?.sort
      ? {
          columnId: reportDefinition.sort.column,
          direction: ['asc', 'desc'].includes(reportDefinition.sort.sortType)
            ? reportDefinition.sort.sortType
            : 'desc',
        }
      : undefined,
  })

  const card = useAnalyticsReportCard({
    reportData,
    isReportDataLoading,
    isReportDefinitionLoading,
    columns,
    reportDefinition,
    activeSeries,
    setActiveSeries,
    headerOptions: headerOptions || [],
  })

  const table = useAnalyticsReportTable({
    reportData,
    isLoading: isReportDataLoading,
    columns,
    sorting,
    activeSeries,
    onSortingChange,
  })

  const handleExport = async () => {
    if (!reportDefinition) {
      return
    }

    setIsExporting(true)
    await exportReportCSV({
      reportId: reportDefinition.id,
      filters: filtersForAPI,
    })
    setIsExporting(false)
  }

  return {
    enabled: isReportDefinitionLoading || reportDefinition,
    externalSource: getExternalSource(reportDefinition?.dataSource),
    decorator: reportDefinition?.decorator,
    isLoading: isReportDefinitionLoading,
    reportId,
    title: reportDefinition?.name || 'Report',
    card,
    table,
    filters: {
      filters,
      onChange: onFiltersChange,
    },
    hasActiveFilters,
    supportsPrecisions,
    params,
    isExporting,
    onExport: handleExport,
  }
}
